import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { AdminSettings } from "@/types/adminSettings";
import { Grid, LayoutGrid } from "lucide-react";

interface DisplayTabProps {
  settings: AdminSettings | null;
  updateSettings: (path: string[], value: any) => void;
  saveSettings: () => void;
  isSaving: boolean;
}

export const DisplayTab = ({ settings, updateSettings, saveSettings, isSaving }: DisplayTabProps) => {
  if (!settings) return null;

  const handleLayoutModeChange = (value: 'uniform' | 'adaptive') => {
    updateSettings(['display', 'layoutMode'], value);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LayoutGrid className="h-5 w-5" />
            Service Card Layout
          </CardTitle>
          <CardDescription>
            Choose how service cards are displayed on the homepage
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <Label>Layout Mode</Label>
            <RadioGroup
              value={settings.display.layoutMode}
              onValueChange={handleLayoutModeChange}
              className="grid grid-cols-1 gap-4"
            >
              <div className="flex items-center space-x-3 border rounded-lg p-4 hover:bg-accent/50 transition-colors">
                <RadioGroupItem value="adaptive" id="adaptive" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <LayoutGrid className="h-4 w-4" />
                    <Label htmlFor="adaptive" className="font-medium">
                      Adaptive Layout
                    </Label>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Cards automatically adjust their shape based on the service image aspect ratio
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 border rounded-lg p-4 hover:bg-accent/50 transition-colors">
                <RadioGroupItem value="uniform" id="uniform" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Grid className="h-4 w-4" />
                    <Label htmlFor="uniform" className="font-medium">
                      Uniform Layout
                    </Label>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    All cards maintain the same square aspect ratio for a consistent grid
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button 
          onClick={saveSettings} 
          disabled={isSaving}
          className="min-w-24"
        >
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
};